'use client';

import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { monitoringEnv } from './env';
import { baseConfig, type BugsnagEventLike } from './shared';

/**
 * Web's browser Bugsnag client.
 *
 * No performance SDK here, unlike Admin. This site is four routes of mostly
 * static marketing plus one live page; `@bugsnag/browser-performance` would add
 * a second SDK and a second set of network calls to pages whose whole point is
 * to load fast, for spans nobody is watching. Error reporting is the part that
 * earns its weight. Revisit if someone starts asking about web vitals here.
 */

let attempted = false;
let started = false;

/**
 * Starts the browser client. Idempotent, never throws, callable from anywhere.
 *
 * Not tied to a `useEffect`: `app/global-error.tsx` replaces the root layout,
 * so by the time it renders the normal provider tree may never have mounted.
 * It calls this itself.
 */
export function startMonitoring(): boolean {
  if (attempted) return started;
  attempted = true;

  if (typeof window === 'undefined') return false;

  if (!monitoringEnv.enabled) {
    console.warn(
      `[monitoring] error reporting is off on "${monitoringEnv.tier}": ${
        monitoringEnv.disabledReason ?? 'disabled'
      }`,
    );
    return false;
  }

  const config = baseConfig(monitoringEnv.apiKey as string, 'browser');

  try {
    Bugsnag.start({
      ...config,
      plugins: [new BugsnagPluginReact()],
      onError: (event) => config.onError(event as unknown as BugsnagEventLike),
    });
    started = true;
  } catch (error) {
    console.warn('[monitoring] Bugsnag.start failed; error reporting is off.', error);
    return false;
  }

  return started;
}

export const isMonitoringStarted = (): boolean => started;

/** Reports a handled error, starting the client first if nothing else has. */
export function notifyError(
  error: unknown,
  metadata?: Record<string, Record<string, unknown>>,
): void {
  if (!started && !startMonitoring()) return;
  const err = error instanceof Error ? error : new Error(String(error));
  try {
    Bugsnag.notify(err, (event) => {
      if (metadata) {
        for (const [section, values] of Object.entries(metadata)) {
          event.addMetadata(section, values);
        }
      }
      return true;
    });
  } catch {
    // Reporting must never throw out of an error boundary.
  }
}

export function leaveBreadcrumb(message: string, metadata?: Record<string, unknown>): void {
  if (!started) return;
  try {
    Bugsnag.leaveBreadcrumb(message, metadata);
  } catch {
    // Best-effort by definition.
  }
}

/**
 * Sends one uniquely tagged handled event, to prove delivery end to end.
 * Search the dashboard for the returned id — a local log line proves nothing.
 */
export function sendMonitoringProbe(label = 'manual'): { sent: boolean; runId: string } {
  const runId = `probe-web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  if (!started && !startMonitoring()) {
    console.warn(`[monitoring] probe ${runId} not sent — monitoring is off.`);
    return { sent: false, runId };
  }
  notifyError(new Error(`Point2 monitoring probe ${runId}`), {
    probe: { runId, label, releaseStage: monitoringEnv.tier, appVersion: monitoringEnv.appVersion },
  });
  console.log(`[monitoring] probe sent. Search Bugsnag for: ${runId}`);
  return { sent: true, runId };
}

export { Bugsnag };
